/**
 * This file contains logic required to load Yuno's SDK into the app.
 *
 * This is modeled similarly to Stripe's @stripe/stripe-js package and our Authorize.net loader.
 * Yuno SDK uses an event-driven approach where it fires a 'yuno-sdk-ready' event when loaded.
 *
 * @see https://docs.y.uno/docs/full-checkout-sdk
 * @see https://docs.y.uno/docs/full-sdk-implementation
 */
/* eslint-disable no-console */

import type { Yuno } from "@yuno-payments/sdk-web-types";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";

import "@shared/lib/yuno/types";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LoadParams {
  // no-op. No known loading params required yet.
}

const YUNO_SDK_URL = "https://sdk-web.y.uno/v1.5/main.js";
const YUNO_SDK_URL_REGEX =
  /^https:\/\/sdk-web\.y\.uno\/v[\d.]+\/main\.js\/?(\?.*)?$/;

const EXISTING_SCRIPT_MESSAGE =
  "loadScript was called but an existing Yuno SDK script already exists in the document; existing script parameters will be used";

/**
 * Responsible for finding an existing Yuno SDK script on the DOM
 */
export const findScript = (): HTMLScriptElement | null => {
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[src*="sdk-web.y.uno"]'
  );

  for (let i = 0; i < scripts.length; i += 1) {
    const script = scripts[i];

    if (!YUNO_SDK_URL_REGEX.test(script.src)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    return script;
  }

  return null;
};

/**
 * Responsible for injecting the Yuno SDK script onto the DOM
 */
const injectScript = (): HTMLScriptElement => {
  const script = document.createElement("script");
  script.src = YUNO_SDK_URL;
  script.defer = true;

  const headOrBody = document.head || document.body;

  if (!headOrBody) {
    throw new Error(
      "Expected document.body not to be null. Yuno SDK requires a <body> element."
    );
  }

  headOrBody.appendChild(script);

  return script;
};

let yunoPromise: Promise<Yuno | null> | null = null;

let onErrorListener: ((event: Event | string) => void) | null = null;
let onReadyListener: (() => void) | null = null;

const onError =
  (reject: (reason?: unknown) => void) => (event: Event | string) => {
    const errorMessage =
      event instanceof Event ? "Script load error" : String(event);
    const error = new Error(`Failed to load Yuno SDK: ${errorMessage}`);

    logAppInfo("Yuno SDK script failed to load", {
      category: "YunoLoader",
      data: {
        url: YUNO_SDK_URL,
        error: errorMessage,
      },
    });
    recordAppError(error);
    reject(error);
  };

const onReady =
  (
    resolve: (value: Yuno | PromiseLike<Yuno | null> | null) => void,
    reject: (reason?: unknown) => void
  ) =>
  () => {
    logAppInfo("Yuno SDK ready event fired, checking window.Yuno", {
      category: "YunoLoader",
    });

    if (window.Yuno) {
      logAppInfo("window.Yuno is available", {
        category: "YunoLoader",
        data: {
          hasInitialize: typeof window.Yuno.initialize === "function",
        },
      });
      resolve(window.Yuno);
    } else {
      const error = new Error("Yuno SDK not available after ready event");
      logAppInfo("Ready event fired but window.Yuno unavailable", {
        category: "YunoLoader",
        data: {
          error: "SDK failed to initialize properly",
        },
      });
      recordAppError(error);
      reject(error);
    }
  };

/**
 * Manages loading the Yuno SDK onto the DOM, from finding existing scripts to attaching event listeners.
 *
 * Yuno SDK uses an event-driven approach:
 * - Fires 'yuno-sdk-ready' event when SDK is loaded and ready
 * - Fires 'yuno-sdk-error' custom event on errors
 */
export const loadScript = async (
  params: null | LoadParams
): Promise<Yuno | null> => {
  // Ensure that we only attempt to load Yuno SDK at most once
  if (yunoPromise !== null) {
    return yunoPromise;
  }

  yunoPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      // Resolve to null when imported server side. This makes the module
      // safe to import in an isomorphic code base.
      logAppInfo("Server-side environment detected", {
        category: "YunoLoader",
      });
      resolve(null);
      return;
    }

    logAppInfo("Starting Yuno SDK load process", {
      category: "YunoLoader",
      data: {
        url: YUNO_SDK_URL,
        environment: process.env.APP_ENV,
        existingYuno: !!window.Yuno,
      },
    });

    if (window.Yuno && params) {
      console.warn(EXISTING_SCRIPT_MESSAGE);
    }

    if (window.Yuno) {
      logAppInfo("window.Yuno already exists, using existing instance", {
        category: "YunoLoader",
      });
      resolve(window.Yuno);
      return;
    }

    try {
      let script = findScript();

      if (script && params) {
        console.warn(EXISTING_SCRIPT_MESSAGE);
      } else if (!script) {
        logAppInfo("No existing script found, injecting new script", {
          category: "YunoLoader",
        });
        script = injectScript();
        logAppInfo("Script injected into DOM", {
          category: "YunoLoader",
          data: {
            src: script.src,
            parentElement: script.parentElement?.tagName,
          },
        });
      } else if (
        script &&
        onReadyListener !== null &&
        onErrorListener !== null
      ) {
        logAppInfo("Existing script found with listeners, reloading", {
          category: "YunoLoader",
        });
        // Remove event listeners
        window.removeEventListener("yuno-sdk-ready", onReadyListener);
        window.removeEventListener("yuno-sdk-error", onErrorListener);
        script.removeEventListener("error", onErrorListener);

        // If script exists, but we are reloading due to an error,
        // reload script to trigger ready event
        script.parentNode?.removeChild(script);
        script = injectScript();
        logAppInfo("Script reloaded", {
          category: "YunoLoader",
        });
      }

      onReadyListener = onReady(resolve, reject);
      // Yuno SDK fires a 'yuno-sdk-ready' event on window when loaded
      window.addEventListener("yuno-sdk-ready", onReadyListener);

      onErrorListener = onError(reject);
      // Listen for both script error and custom yuno-sdk-error event
      script.addEventListener("error", onErrorListener);
      window.addEventListener("yuno-sdk-error", onErrorListener);

      logAppInfo("Event listeners attached, waiting for Yuno SDK to load", {
        category: "YunoLoader",
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      logAppInfo("Error during script loading setup", {
        category: "YunoLoader",
        data: {
          error: errorMessage,
        },
      });
      recordAppError(error instanceof Error ? error : new Error(String(error)));
      reject(error);
    }
  });

  // Resets yunoPromise on error
  return yunoPromise.catch((error) => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logAppInfo("Failed to load Yuno SDK", {
      category: "YunoLoader",
      data: {
        error: errorMessage,
      },
    });
    yunoPromise = null;
    return Promise.reject(error);
  });
};
